import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { canAccessFeature, isCompanyAdmin } from '../utils/planAccess';
import UpgradeCta from './UpgradeCta';
import { useConfirm } from '../context/ConfirmContext';
import FortnoxInvoiceSection from './FortnoxInvoiceSection';
import { formatDateTime } from '../utils/locale';
import './IntegrationsSection.css';

const formatMemberLabel = (row) => {
  const name = `${row.firstName || ''} ${row.lastName || ''}`.trim();
  return name || row.email;
};

const IntegrationsSection = () => {
  const confirm = useConfirm();
  const { t } = useTranslation(['company', 'common']);
  const { user } = useContext(AuthContext);
  const [endpoints, setEndpoints] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedEndpointId, setExpandedEndpointId] = useState(null);
  const [deliveries, setDeliveries] = useState({});
  const [createdKeyPlaintext, setCreatedKeyPlaintext] = useState('');
  const [fortnoxStatus, setFortnoxStatus] = useState({
    configured: false,
    connected: false,
    connectedAt: null,
    scope: null,
    requiredScopes: [],
    missingScopes: [],
    needsReconnect: false,
    cache: null
  });
  const [employeeMaps, setEmployeeMaps] = useState([]);
  const [mapDrafts, setMapDrafts] = useState({});
  const [fortnoxBusy, setFortnoxBusy] = useState(false);
  const [referenceTab, setReferenceTab] = useState('customers');
  const [referenceSearch, setReferenceSearch] = useState('');
  const [referenceSearchDebounced, setReferenceSearchDebounced] = useState('');
  const [referencePage, setReferencePage] = useState(1);
  const [referenceRows, setReferenceRows] = useState([]);
  const [referenceMeta, setReferenceMeta] = useState({
    page: 1,
    totalPages: 1,
    totalResources: 0,
    source: null,
    syncedAt: null
  });
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [form, setForm] = useState({
    url: '',
    secret: '',
    subscribedEvents: ['task.assigned', 'task.status_changed'],
    enabled: true
  });
  const [keyForm, setKeyForm] = useState({
    name: '',
    keyType: 'read'
  });

  const getEventLabel = (eventType) => {
    const labels = {
      'task.created': 'company:eventTaskCreated',
      'task.assigned': 'company:eventTaskAssigned',
      'task.status_changed': 'company:eventTaskStatusChanged',
      'task.check_in': 'company:eventTaskCheckIn',
      'task.pause_checkout': 'company:eventTaskPauseCheckout',
      'task.check_out': 'company:eventTaskCheckOut'
    };
    const key = labels[eventType];
    return key ? t(key) : eventType;
  };

  const hasAccess = isCompanyAdmin(user) && canAccessFeature(user, 'customIntegrations');

  const fetchData = async () => {
    if (!hasAccess) {
      setLoading(false);
      return;
    }

    try {
      const [webhooksRes, eventsRes, keysRes] = await Promise.all([
        axios.get('/api/integrations/webhooks'),
        axios.get('/api/integrations/event-types'),
        axios.get('/api/integrations/api-keys')
      ]);
      setEndpoints(webhooksRes.data.data);
      setEventTypes(eventsRes.data.data);
      setApiKeys(keysRes.data.data);

      try {
        const [fortnoxRes, mapsRes] = await Promise.all([
          axios.get('/api/integrations/fortnox/status'),
          axios.get('/api/integrations/fortnox/employee-maps')
        ]);
        setFortnoxStatus(fortnoxRes.data.data || { configured: false, connected: false, needsReconnect: false });
        const maps = mapsRes.data.data || [];
        setEmployeeMaps(maps);
        setMapDrafts(
          Object.fromEntries(maps.map((row) => [row.userId, row.fortnoxEmployeeId || '']))
        );
      } catch (fortnoxErr) {
        setError(formatError(fortnoxErr) || t('company:failedLoadFortnox'));
      }
    } catch (err) {
      setError(formatError(err) || t('company:failedLoadIntegrations'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [hasAccess, t]);

  useEffect(() => {
    if (!hasAccess) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const fortnoxResult = params.get('fortnox');
    if (!fortnoxResult) {
      return;
    }

    if (fortnoxResult === 'connected') {
      setSuccess(t('company:fortnoxConnectedSuccess'));
    } else if (fortnoxResult === 'error') {
      setError(params.get('message') || t('company:fortnoxConnectionFailed'));
    }

    params.delete('fortnox');
    params.delete('message');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || '#fortnox'}`;
    window.history.replaceState({}, '', nextUrl);
  }, [hasAccess, t]);

  const canBrowseReferenceData = fortnoxStatus.connected
    && !fortnoxStatus.needsReconnect
    && fortnoxStatus.configured;

  const referenceLoadIdRef = useRef(0);

  const loadReferenceData = useCallback(async ({
    tab = referenceTab,
    page = referencePage,
    search = referenceSearchDebounced
  } = {}) => {
    if (!fortnoxStatus.connected || fortnoxStatus.needsReconnect || !fortnoxStatus.configured) {
      return;
    }

    const requestId = referenceLoadIdRef.current + 1;
    referenceLoadIdRef.current = requestId;
    setReferenceLoading(true);

    try {
      const endpoint = tab === 'articles'
        ? '/api/integrations/fortnox/articles'
        : '/api/integrations/fortnox/customers';
      const response = await axios.get(endpoint, {
        params: { page, limit: 25, search: search || undefined }
      });
      if (requestId !== referenceLoadIdRef.current) {
        return;
      }
      const payload = response.data?.data || {};
      setReferenceRows(payload.items || []);
      setReferenceMeta({
        page: payload.page || page,
        totalPages: payload.totalPages || 1,
        totalResources: payload.totalResources || 0,
        source: payload.source || null,
        syncedAt: payload.syncedAt || null
      });
    } catch (err) {
      if (requestId !== referenceLoadIdRef.current) {
        return;
      }
      setError(formatError(err) || t('company:fortnoxReferenceLoadFailed'));
      setReferenceRows([]);
    } finally {
      if (requestId === referenceLoadIdRef.current) {
        setReferenceLoading(false);
      }
    }
  }, [
    fortnoxStatus.connected,
    fortnoxStatus.needsReconnect,
    fortnoxStatus.configured,
    referenceTab,
    referencePage,
    referenceSearchDebounced,
    t
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setReferenceSearchDebounced(referenceSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [referenceSearch]);

  useEffect(() => {
    if (!canBrowseReferenceData) {
      setReferenceRows([]);
      return;
    }
    loadReferenceData();
  }, [canBrowseReferenceData, loadReferenceData]);

  if (!isCompanyAdmin(user)) {
    return null;
  }

  if (!canAccessFeature(user, 'customIntegrations')) {
    return (
      <div className="card integrations-section" id="integrations">
        <h2>{t('company:integrationsTitle')}</h2>
        <p>
          {t('company:integrationsUpgradeHint')}
        </p>
        <UpgradeCta />
      </div>
    );
  }

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleKeyFormChange = (e) => {
    const { name, value } = e.target;
    setKeyForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleConnectFortnox = async ({ billing = false } = {}) => {
    setError('');
    setSuccess('');
    setFortnoxBusy(true);
    try {
      const response = await axios.get('/api/integrations/fortnox/connect', {
        params: billing ? { billing: 'true' } : undefined
      });
      const authorizeUrl = response.data?.data?.authorizeUrl;
      if (!authorizeUrl) {
        setError(t('company:fortnoxAuthorizeUrlMissing'));
        setFortnoxBusy(false);
        return;
      }
      window.location.href = authorizeUrl;
    } catch (err) {
      setError(formatError(err) || t('company:failedStartFortnox'));
      setFortnoxBusy(false);
    }
  };

  const handleDisconnectFortnox = async () => {
    if (!(await confirm(t('company:confirmDisconnectFortnox'), { danger: true }))) {
      return;
    }
    setError('');
    setSuccess('');
    setFortnoxBusy(true);
    try {
      await axios.delete('/api/integrations/fortnox');
      setFortnoxStatus((prev) => ({ ...prev, connected: false, connectedAt: null }));
      setSuccess(t('company:fortnoxDisconnected'));
    } catch (err) {
      setError(formatError(err) || t('company:failedDisconnectFortnox'));
    } finally {
      setFortnoxBusy(false);
    }
  };

  const handleMapDraftChange = (userId, value) => {
    setMapDrafts((prev) => ({ ...prev, [userId]: value }));
  };

  const handleReferenceTabChange = (tab) => {
    setReferenceTab(tab);
    setReferencePage(1);
  };

  const handleReferenceSearchChange = (e) => {
    setReferenceSearch(e.target.value);
    setReferencePage(1);
  };

  const handleRefreshReferenceData = async () => {
    setError('');
    setSuccess('');
    setFortnoxBusy(true);
    try {
      const response = await axios.post('/api/integrations/fortnox/sync-reference-data');
      const { customers, articles } = response.data?.data || {};
      const statusRes = await axios.get('/api/integrations/fortnox/status');
      setFortnoxStatus(statusRes.data.data || fortnoxStatus);
      setSuccess(t('company:fortnoxReferenceRefreshSuccess', {
        customers: customers ?? 0,
        articles: articles ?? 0
      }));
      await loadReferenceData({ page: 1 });
      setReferencePage(1);
    } catch (err) {
      setError(formatError(err) || t('company:fortnoxReferenceRefreshFailed'));
    } finally {
      setFortnoxBusy(false);
    }
  };

  const handleSaveEmployeeMaps = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setFortnoxBusy(true);
    try {
      const mappings = employeeMaps.map((row) => ({
        userId: row.userId,
        fortnoxEmployeeId: mapDrafts[row.userId] || ''
      }));
      await axios.put('/api/integrations/fortnox/employee-maps', { mappings });
      const mapsRes = await axios.get('/api/integrations/fortnox/employee-maps');
      const maps = mapsRes.data.data || [];
      setEmployeeMaps(maps);
      setMapDrafts(
        Object.fromEntries(maps.map((row) => [row.userId, row.fortnoxEmployeeId || '']))
      );
      setSuccess(t('company:fortnoxMappingsSaved'));
    } catch (err) {
      setError(formatError(err) || t('company:failedSaveFortnoxMappings'));
    } finally {
      setFortnoxBusy(false);
    }
  };

  const handleEventToggle = (eventType) => {
    setForm((prev) => {
      const selected = new Set(prev.subscribedEvents);
      if (selected.has(eventType)) {
        selected.delete(eventType);
      } else {
        selected.add(eventType);
      }
      return { ...prev, subscribedEvents: Array.from(selected) };
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/integrations/webhooks', form);
      setEndpoints((prev) => [response.data.data, ...prev]);
      setForm({
        url: '',
        secret: '',
        subscribedEvents: ['task.assigned', 'task.status_changed'],
        enabled: true
      });
      setSuccess(t('company:webhookCreated'));
    } catch (err) {
      setError(formatError(err) || t('company:failedCreateWebhook'));
    }
  };

  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/integrations/api-keys', keyForm);
      const { key, ...metadata } = response.data.data;
      setApiKeys((prev) => [metadata, ...prev]);
      setCreatedKeyPlaintext(key);
      setKeyForm({ name: '', keyType: 'read' });
      setSuccess(t('company:apiKeyCreated'));
    } catch (err) {
      setError(formatError(err) || t('company:failedCreateApiKey'));
    }
  };

  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(createdKeyPlaintext);
      setSuccess(t('company:apiKeyCopied'));
    } catch (err) {
      setError(t('company:failedCopyApiKey'));
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!(await confirm(t('company:confirmRevokeApiKey'), { danger: true }))) {
      return;
    }

    try {
      const response = await axios.delete(`/api/integrations/api-keys/${keyId}`);
      setApiKeys((prev) => prev.map((item) => (
        item.id === keyId ? response.data.data : item
      )));
      setSuccess(t('company:apiKeyRevoked'));
    } catch (err) {
      setError(formatError(err) || t('company:failedRevokeApiKey'));
    }
  };

  const handleDelete = async (endpointId) => {
    if (!(await confirm(t('company:confirmDeleteWebhook'), { danger: true }))) {
      return;
    }

    try {
      await axios.delete(`/api/integrations/webhooks/${endpointId}`);
      setEndpoints((prev) => prev.filter((endpoint) => endpoint.id !== endpointId));
      setSuccess(t('company:webhookDeleted'));
    } catch (err) {
      setError(formatError(err) || t('company:failedDeleteWebhook'));
    }
  };

  const handleToggleEnabled = async (endpoint) => {
    try {
      const response = await axios.put(`/api/integrations/webhooks/${endpoint.id}`, {
        enabled: !endpoint.enabled
      });
      setEndpoints((prev) => prev.map((item) => (
        item.id === endpoint.id ? response.data.data : item
      )));
    } catch (err) {
      setError(formatError(err) || t('company:failedUpdateWebhook'));
    }
  };

  const loadDeliveries = async (endpointId) => {
    if (expandedEndpointId === endpointId) {
      setExpandedEndpointId(null);
      return;
    }

    try {
      const response = await axios.get(`/api/integrations/webhooks/${endpointId}/deliveries`);
      setDeliveries((prev) => ({ ...prev, [endpointId]: response.data.data }));
      setExpandedEndpointId(endpointId);
    } catch (err) {
      setError(formatError(err) || t('company:failedLoadDeliveryLog'));
    }
  };

  const activeKeys = apiKeys.filter((key) => !key.revokedAt);
  const revokedKeys = apiKeys.filter((key) => key.revokedAt);

  return (
    <div className="card integrations-section" id="integrations">
      <h2>{t('company:integrationsTitle')}</h2>
      <p className="section-description">
        {t('company:integrationsDescription')}
      </p>

      {loading ? (
        <p>{t('company:loadingIntegrations')}</p>
      ) : (
        <>
          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}

          <div className="fortnox-block" id="fortnox">
            <h3>{t('company:fortnoxTitle')}</h3>
            <p className="section-description">
              {t('company:fortnoxDescription')}
            </p>

            <div className="fortnox-status-row">
              <p>
                {t('company:fortnoxStatusLabel')}
                {' '}
                {!fortnoxStatus.configured
                  ? t('company:fortnoxNotConfigured')
                  : fortnoxStatus.connected
                    ? `${t('company:fortnoxConnected')}${fortnoxStatus.connectedAt ? ` (${formatDateTime(fortnoxStatus.connectedAt)})` : ''}`
                    : t('company:fortnoxNotConnected')}
              </p>
              <div className="endpoint-actions">
                {fortnoxStatus.connected ? (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDisconnectFortnox}
                    disabled={fortnoxBusy}
                  >
                    {t('company:fortnoxDisconnect')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleConnectFortnox()}
                    disabled={fortnoxBusy || !fortnoxStatus.configured}
                  >
                    {t('company:fortnoxConnect')}
                  </button>
                )}
              </div>
            </div>

            {fortnoxStatus.connected && fortnoxStatus.scope && (
              <p className="fortnox-scopes">
                {t('company:fortnoxScopesLabel')}
                {' '}
                <code>{fortnoxStatus.scope}</code>
              </p>
            )}

            {fortnoxStatus.needsReconnect && (
              <div className="fortnox-reconnect-banner" role="status">
                <p>{t('company:fortnoxReconnectWarning')}</p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleConnectFortnox({ billing: true })}
                  disabled={fortnoxBusy || !fortnoxStatus.configured}
                >
                  {t('company:fortnoxReconnect')}
                </button>
              </div>
            )}

            {fortnoxStatus.connected && !fortnoxStatus.needsReconnect && (
              <div className="fortnox-reference-block">
                <h4>{t('company:fortnoxReferenceData')}</h4>
                <p className="section-description">{t('company:fortnoxReferenceDataHint')}</p>
                <div className="fortnox-reference-toolbar">
                  <div className="fortnox-reference-tabs">
                    <button
                      type="button"
                      className={`btn btn-secondary${referenceTab === 'customers' ? ' active' : ''}`}
                      onClick={() => handleReferenceTabChange('customers')}
                    >
                      {t('company:fortnoxCustomersTab')}
                    </button>
                    <button
                      type="button"
                      className={`btn btn-secondary${referenceTab === 'articles' ? ' active' : ''}`}
                      onClick={() => handleReferenceTabChange('articles')}
                    >
                      {t('company:fortnoxArticlesTab')}
                    </button>
                  </div>
                  <input
                    type="search"
                    value={referenceSearch}
                    onChange={handleReferenceSearchChange}
                    placeholder={t('company:fortnoxSearchPlaceholder')}
                    aria-label={t('company:fortnoxSearchPlaceholder')}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleRefreshReferenceData}
                    disabled={fortnoxBusy}
                  >
                    {t('company:fortnoxRefreshReferenceData')}
                  </button>
                </div>
                <p className="fortnox-reference-sync-hint">
                  {(referenceMeta.syncedAt
                    || (referenceTab === 'articles'
                      ? fortnoxStatus.cache?.articlesSyncedAt
                      : fortnoxStatus.cache?.customersSyncedAt))
                    ? t('company:fortnoxReferenceSyncedAt', {
                      date: formatDateTime(
                        referenceMeta.syncedAt
                        || (referenceTab === 'articles'
                          ? fortnoxStatus.cache?.articlesSyncedAt
                          : fortnoxStatus.cache?.customersSyncedAt)
                      )
                    })
                    : t('company:fortnoxReferenceNotSynced')}
                  {referenceMeta.source ? ` (${referenceMeta.source})` : ''}
                </p>
                {referenceLoading ? (
                  <p>{t('company:loadingIntegrations')}</p>
                ) : referenceRows.length === 0 ? (
                  <p>{t('company:fortnoxNoReferenceRows')}</p>
                ) : (
                  <div className="fortnox-maps-table-wrap">
                    <table className="fortnox-maps-table">
                      <thead>
                        <tr>
                          {referenceTab === 'customers' ? (
                            <>
                              <th>{t('company:fortnoxCustomerNumber')}</th>
                              <th>{t('company:fortnoxCustomerName')}</th>
                              <th>{t('company:fortnoxCustomerOrgNumber')}</th>
                            </>
                          ) : (
                            <>
                              <th>{t('company:fortnoxArticleNumber')}</th>
                              <th>{t('company:fortnoxArticleDescription')}</th>
                              <th>{t('company:fortnoxArticleUnit')}</th>
                              <th>{t('company:fortnoxArticlePrice')}</th>
                            </>
                          )}
                          <th>{t('company:fortnoxActiveColumn')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referenceRows.map((row) => (
                          <tr key={row.number}>
                            {referenceTab === 'customers' ? (
                              <>
                                <td>{row.number}</td>
                                <td>{row.name}</td>
                                <td>{row.organisationNumber || '—'}</td>
                              </>
                            ) : (
                              <>
                                <td>{row.number}</td>
                                <td>{row.description}</td>
                                <td>{row.unit || '—'}</td>
                                <td>{row.salesPrice != null ? row.salesPrice : '—'}</td>
                              </>
                            )}
                            <td>{row.active ? t('common:active') : t('common:inactive')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {referenceMeta.totalPages > 1 && (
                  <div className="fortnox-reference-pagination">
                    <p>{t('company:fortnoxPageInfo', {
                      page: referenceMeta.page,
                      totalPages: referenceMeta.totalPages,
                      total: referenceMeta.totalResources
                    })}
                    </p>
                    <div className="endpoint-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={referencePage <= 1 || referenceLoading}
                        onClick={() => setReferencePage((prev) => Math.max(1, prev - 1))}
                      >
                        {t('company:fortnoxPreviousPage')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={referencePage >= referenceMeta.totalPages || referenceLoading}
                        onClick={() => setReferencePage((prev) => prev + 1)}
                      >
                        {t('company:fortnoxNextPage')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {canBrowseReferenceData && (
              <FortnoxInvoiceSection disabled={fortnoxBusy} />
            )}

            {fortnoxStatus.connected && fortnoxStatus.needsReconnect && (
              <p className="fortnox-scope-missing">{t('company:fortnoxScopeMissing')}</p>
            )}

            <form className="endpoint-form fortnox-maps-form" onSubmit={handleSaveEmployeeMaps}>
              <h4>{t('company:fortnoxEmployeeMapping')}</h4>
              {employeeMaps.length === 0 ? (
                <p>{t('company:fortnoxNoUsersToMap')}</p>
              ) : (
                <div className="fortnox-maps-table-wrap">
                  <table className="fortnox-maps-table">
                    <thead>
                      <tr>
                        <th>{t('company:fortnoxUserColumn')}</th>
                        <th>{t('common:email')}</th>
                        <th>{t('company:fortnoxEmployeeIdColumn')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeMaps.map((row) => (
                        <tr key={row.userId}>
                          <td>{formatMemberLabel(row)}</td>
                          <td>{row.email}</td>
                          <td>
                            <input
                              type="text"
                              value={mapDrafts[row.userId] || ''}
                              onChange={(e) => handleMapDraftChange(row.userId, e.target.value)}
                              placeholder={t('company:fortnoxEmployeeIdPlaceholder')}
                              aria-label={t('company:fortnoxEmployeeIdAria', { name: formatMemberLabel(row) })}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={fortnoxBusy || employeeMaps.length === 0}
              >
                {t('company:fortnoxSaveMappings')}
              </button>
            </form>
          </div>

          <h3>{t('company:webhooksTitle')}</h3>
          <form className="endpoint-form" onSubmit={handleCreate}>
            <div className="form-group">
              <label htmlFor="webhook-url">{t('company:webhookUrlLabel')}</label>
              <input
                id="webhook-url"
                name="url"
                type="url"
                value={form.url}
                onChange={handleFormChange}
                placeholder={t('company:webhookUrlPlaceholder')}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="webhook-secret">{t('company:webhookSecretLabel')}</label>
              <input
                id="webhook-secret"
                name="secret"
                type="password"
                value={form.secret}
                onChange={handleFormChange}
                minLength={16}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('company:subscribedEvents')}</label>
              <div className="event-checkboxes">
                {eventTypes.map((eventType) => (
                  <label key={eventType} className="event-checkbox">
                    <input
                      type="checkbox"
                      checked={form.subscribedEvents.includes(eventType)}
                      onChange={() => handleEventToggle(eventType)}
                    />
                    <span>{getEventLabel(eventType)}</span>
                  </label>
                ))}
              </div>
            </div>

            <button type="submit" className="btn btn-primary">{t('company:addWebhookEndpoint')}</button>
          </form>

          <div className="endpoint-list">
            {endpoints.length === 0 ? (
              <p>{t('company:noWebhookEndpoints')}</p>
            ) : (
              endpoints.map((endpoint) => (
                <div key={endpoint.id} className="endpoint-card">
                  <div className="endpoint-header">
                    <div>
                      <div className="endpoint-url">{endpoint.url}</div>
                      <p>{endpoint.enabled ? t('common:enabled') : t('common:disabled')}</p>
                      <p>
                        {t('company:eventsLabel')}
                        {' '}
                        {endpoint.subscribedEvents.map((event) => getEventLabel(event)).join(', ')}
                      </p>
                    </div>
                    <div className="endpoint-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => handleToggleEnabled(endpoint)}
                      >
                        {endpoint.enabled ? t('common:disabled') : t('common:enabled')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => loadDeliveries(endpoint.id)}
                      >
                        {expandedEndpointId === endpoint.id ? t('company:hideLog') : t('company:deliveryLog')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => handleDelete(endpoint.id)}
                      >
                        {t('common:delete')}
                      </button>
                    </div>
                  </div>

                  {expandedEndpointId === endpoint.id && (
                    <div className="delivery-log">
                      {(deliveries[endpoint.id] || []).length === 0 ? (
                        <p>{t('company:noDeliveries')}</p>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>{t('company:deliveryTime')}</th>
                              <th>{t('company:deliveryEvent')}</th>
                              <th>{t('company:deliveryStatus')}</th>
                              <th>{t('company:deliveryAttempt')}</th>
                              <th>{t('company:deliveryDetails')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(deliveries[endpoint.id] || []).map((entry) => (
                              <tr key={entry.id}>
                                <td>{formatDateTime(entry.deliveredAt)}</td>
                                <td>{entry.eventType}</td>
                                <td className={entry.status === 'success' ? 'status-success' : 'status-failure'}>
                                  {entry.status}
                                </td>
                                <td>{entry.attempt}</td>
                                <td>
                                  {entry.httpStatus ? `HTTP ${entry.httpStatus}` : ''}
                                  {entry.errorMessage ? ` ${entry.errorMessage}` : ''}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <h3 className="api-keys-heading">{t('company:apiKeysTitle')}</h3>
          <p className="section-description">
            {t('company:apiKeysDescription')}
          </p>

          {createdKeyPlaintext && (
            <div className="created-key-banner" role="status">
              <p><strong>{t('company:copyApiKeyNow')}</strong> {t('company:copyApiKeyHint')}</p>
              <code className="created-key-value">{createdKeyPlaintext}</code>
              <div className="endpoint-actions">
                <button type="button" className="btn btn-primary" onClick={handleCopyKey}>
                  {t('company:copyKey')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCreatedKeyPlaintext('')}
                >
                  {t('company:dismiss')}
                </button>
              </div>
            </div>
          )}

          <form className="endpoint-form" onSubmit={handleCreateApiKey}>
            <div className="form-group">
              <label htmlFor="api-key-name">{t('company:apiKeyLabel')}</label>
              <input
                id="api-key-name"
                name="name"
                type="text"
                value={keyForm.name}
                onChange={handleKeyFormChange}
                placeholder={t('company:apiKeyLabelPlaceholder')}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="api-key-type">{t('company:apiKeyAccess')}</label>
              <select
                id="api-key-type"
                name="keyType"
                value={keyForm.keyType}
                onChange={handleKeyFormChange}
              >
                <option value="read">{t('company:apiKeyReadOnly')}</option>
                <option value="write">{t('company:apiKeyReadWrite')}</option>
              </select>
            </div>
            <button type="submit" className="btn btn-primary">{t('company:createApiKey')}</button>
          </form>

          <div className="endpoint-list">
            {activeKeys.length === 0 ? (
              <p>{t('company:noActiveApiKeys')}</p>
            ) : (
              activeKeys.map((key) => (
                <div key={key.id} className="endpoint-card">
                  <div className="endpoint-header">
                    <div>
                      <div className="endpoint-url">{key.name}</div>
                      <p>
                        {t('company:keyPrefix')}
                        {' '}
                        <code>{key.keyPrefix}…</code>
                        {' · '}
                        {key.keyType === 'write' ? t('company:apiKeyReadWrite') : t('company:apiKeyReadOnly')}
                      </p>
                      <p>
                        {t('company:lastUsed')}
                        {' '}
                        {key.lastUsedAt ? formatDateTime(key.lastUsedAt) : t('common:never')}
                      </p>
                      <p>
                        {t('company:created')}
                        {' '}
                        {formatDateTime(key.createdAt)}
                      </p>
                    </div>
                    <div className="endpoint-actions">
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => handleRevokeApiKey(key.id)}
                      >
                        {t('company:revoke')}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {revokedKeys.length > 0 && (
            <div className="revoked-keys">
              <h4>{t('company:revokedKeysTitle')}</h4>
              <ul>
                {revokedKeys.map((key) => (
                  <li key={key.id}>
                    {key.name}
                    {' '}
                    (<code>{key.keyPrefix}…</code>
                    )
                    {' — '}
                    {t('company:revokedAt', { date: formatDateTime(key.revokedAt) })}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IntegrationsSection;
